//  ----------      START   IMPORT  BLOCK       ----------
import { useEffect, useState, useMemo } from "react";
import debounce from "lodash/debounce";
//  ----------      END   IMPORT  BLOCK       ----------

/**
 * Generic hook for debounced data fetching.
 *
 * @param {string} query - The search string.
 * @param {function} fetchFn - Async function returning an Axios response or data.
 * @param {number} [debounceWait=300] - Debounce delay in ms.
 * @returns {Array} Returns an array containing [data, setData, error].
 */

//  Helper function
const isQueryValid = (str, minLength) => {
  // Check if it's null/empty/just spaces OR if it's too short OR if it contains invalid characters.
  const trimmedStr = str ? str.trim() : '';
  
  if (trimmedStr.length <= minLength) return false;
  
  // Check: non-empty, and does it only contain alphanumeric characters (including spaces)?
  return /^[\p{L}\p{N}\s]+$/u.test(trimmedStr);
};

const useDebouncedFetch = (query, fetchFn, debounceWait = 300) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const minimum_query_lenght = 3;

  const fetchData = useMemo(() => {
    if (typeof fetchFn !== "function") {
      console.warn("useDebouncedFetch: fetchFn is not a function:", fetchFn);
      const noop = () => {};
      noop.cancel = () => {};
      return noop;
    }

    const fn = async (q, signal) => {
      try {
        const response = await fetchFn(q, signal);
        const result = response?.data ?? response;
        console.log("[useDebouncedFetch] ✅ Query result:", result);
        setData(result);
        setError(null);
      } catch (e) {
        if (signal?.aborted) return;
        console.error("[useDebouncedFetch] ❌ Error:", e);
        setError(e);
        setData(null);
      }
    };

    const debounced = debounce(fn, debounceWait);
    return debounced;
  }, [fetchFn, debounceWait]);

useEffect(() => {
    // 💥 CORRECT PLACEMENT 💥: Use the helper function here to validate the query immediately.
    if (!isQueryValid(query, minimum_query_lenght)) {
      // If the query is invalid, cancel any pending debounced calls and clear state.
      fetchData.cancel?.(); 
      setData(null);
      setError(null);
      return;
    }

    const controller = new AbortController();
    fetchData(query, controller.signal);

    return () => {
      controller.abort();
      fetchData.cancel?.();
    };
  }, [query, fetchData]); // fetchData is in the dependency array because it's wrapped in useMemo

  return [data, setData, error];
};

export default useDebouncedFetch;
